import React, {useState, useContext} from "react"
import PropTypes from "prop-types"
import Media from "react-media"

// context and data
import {ShopContext} from "../../../context/ShopContext"
import data from "../../../data"

// components
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Customize from "../../views/Catalogue/Activity/Customize"
import {Button, Link, Money, Overlay} from "./../../controls"
import {Redirect} from "react-router-dom"

// styling and images
import "./CheckoutItems.scss"
import CouponIcon from "../../../images/js/Coupon"
import {faPen, faTrash} from "@fortawesome/free-solid-svg-icons";


function CheckoutItems({showActions, showShipping, showTotal, showVoucher, showShippingEta, showAddMore}) {
  // shop context
  const shop = useContext(ShopContext)

  // state
  const [activityToCustomize, setActivityToCustomize] = useState(null);
  const [redirectTo, setRedirectTo] = useState(null);

  // redirect?
  if(redirectTo) {
    return <Redirect to={redirectTo} />
  }
  
  // no activities? inform the user 
  if(shop.cartActivities.length === 0) {
    return (
      <div id="s-checkoutitems-empty">
        <h2>... ist leer</h2>
        <p>
          Es liegt noch nichts in deinem Warenkorb, aber das können wir leicht ändern:
        </p>
        <div className="back-to-search">
          <Button kind="ok" onClick={() => setRedirectTo("/")} wide={true}>Erlebnisse anschauen</Button>
        </div>
      </div>
    )
  }
  
  // do we have an activity package?
  const activityPackageInfo = data.Cart.getAdventure().ActivityPackage;

  // gather activities as table rows
  let activities = shop.cartActivities
  .filter(a => {
    // if we have an activity package, we do not display activities contained in there
    if(activityPackageInfo) {
      if(activityPackageInfo.activityIds.includes(a.Activity_ID)) {
        return false;
      }
    }
    return true;
  })
  .sort((a, b) => a.ActivityInfo.title > b.ActivityInfo.title ? 1 : -1)
  .map(a => {
    // TODO #cleanup pretty certain some information could / should be prepared in the Cart logic
    let summary = data.Cart.getAdventureActivitySummary(a)
    let image = a.ActivityInfo.images.find(img => img.default) || a.ActivityInfo.images[0]
    let priceConfiguration = a.Activity.priceConfigurations.find(pc => pc.participantCount === a.ParticipantCount)

    let lis = [
      <li key="region">{summary.region}</li>,
      <li key="participants">{`${summary.participantCount} Teilnehmer:`} <Money amount={priceConfiguration.price} showCurrency={false} /></li>,
    ]
    summary.options.forEach(o => {
      lis.push(
        <li key={`opt_${o.id}`}>{`${o.unitCount} × ${o.title}:`} <Money amount={o.price} showCurrency={false} /></li>
      )
    })
    const sas = a.Subactivities || []
    sas.filter(sa => sa.isOptional).forEach(sa => {
      lis.push(
        <li key={`sa_${sa.activity_id}`}>{`${sa.participantCount} × ${sa.title}:`} <Money amount={sa.price} showCurrency={false} /></li>
      )
    })

    // actions
    let actions = null
    if(showActions) {
      const activityRemove = () => shop.cart.removeActivity(a._parent_id);
      const activityCustomize = () => setActivityToCustomize(data.Catalogue.getActivityById(a._parent_id));
      const actionsSmall = <>
        <div className="actions small">
          <Button kind="delete" size="small" onClick={activityRemove}><FontAwesomeIcon icon={faTrash} /></Button>
          <Button size="small" onClick={activityCustomize}><FontAwesomeIcon icon={faPen} /></Button>
        </div>
      </>
      const actionsLarge = <>
        <div className="actions">
          <Button kind="delete" onClick={activityRemove}>entfernen</Button>
          <Button onClick={activityCustomize}>anpassen</Button>
        </div>
      </>
      actions = <Media queries={{large: "(min-width: 640px)"}}>
        {matches => {
          if(matches.large) {
            return actionsLarge;
          }
          else {
            return actionsSmall;
          }
        }}
      </Media>
      
    }

    // return activity as a row
    return (
      <tr key={a.Activity_ID}>
        <td className="image">
          <Link to={`/activity/${a.ActivityInfo.slug}`}>
            <img src={image.url} alt={image.seo["de"]} />
          </Link>
        </td>
        <td>
          <div className="title">{a.ActivityInfo.title}</div>
          <ul>
            {lis}
          </ul>
        </td>
        <td>
          <div><Money amount={a.CalculatedPrice} showCurrency={true} /></div>
          <div>{actions}</div>
          
        </td>
      </tr>
    )
  })

  // shipping information (as table row)
  let shippingInfo = null
  let s =  data.Cart.getShippingType() || {price:0}
  if(showShipping || s.price > 0) {
    let info = data.Cart.getShippingInfo()
    let shippingTypeInfo = data.ShippingTypes.getInfo(info.code) // TODO this should yield all the necessary info ... i.e. there should be no need for data.Cart.getShippingInfo() ... or the other way round
    
    shippingInfo = (
      <tr key="shipping-info">
        <td className="image">
          <img src={shippingTypeInfo.imageUrl} alt="Verpackung" />
        </td>
        <td>
          <div className="title">Verpackung & Versand</div>
          <ul>
            <li>{s.title["de"]}</li>
            <li>{info.how}</li>
          </ul>
        </td>
        <td>
          <Money amount={s.price} />
        </td>
      </tr>
    )
  }

  // shipping eta
  let shippingEta = null
  if(showShippingEta) {
    let info = data.Cart.getShippingInfo()
    shippingEta = (
      <div id="s-checkoutitems-shipping-eta">
        Voraussichtliches Lieferdatum: <span className="when">{info.when}</span>
      </div>
    )
  }

  // voucher
  let voucher = data.Cart.getVoucher()
  let voucherRow
  if(voucher && showVoucher) {
    let discount = data.Cart.getValue("totalDiscount") || 0 // because having a voucher does not necessarily mean it is valid (i.e. if the adventure cost is too low)

    let voucherInvalidDiv = null
    let voucherValidation = data.Cart.getVoucherValidation()
    if(voucherValidation.valid === false) {
      voucherInvalidDiv = (
        <div className="label-error">{voucherValidation.message}</div>
      )
    }

    let voucherActionsDiv = null
    if(showActions) {
      voucherActionsDiv = (
        <div className="actions">
          <Button 
            label="entfernen" 
            kind="delete"  
            onClick={() => shop.cart.setVoucher(null)} />
        </div>
      )
    }
  
    if(voucher) {
      voucherRow = (
        <tr key="voucher">
          <td>
            <div className="voucher-image">
              <CouponIcon />
            </div>
          </td>
          <td>
            <div className="title">Promocode '{voucher.code}'</div>
            {voucherInvalidDiv}
            {voucherActionsDiv}
          </td>
          <td>
            - <Money amount={discount} />
          </td>
        </tr>
      )
    }
  }

  // activity package included?
  let activityPackageRow = null;
  let activityPackageDiscountRow = null;
  if(activityPackageInfo) {
    // actions
    let packageActions = null
    
    if(showActions) {
      const packageRemove = () => shop.cart.removeActivityPackage(activityPackageInfo.activityPackage);
      const packageCustomize = () => setRedirectTo(`/packages/${activityPackageInfo.activityPackage.slug}`);
      const actionsSmall = <>
        <div className="actions small">
          <Button kind="delete" size="small" onClick={packageRemove}><FontAwesomeIcon icon={faTrash} /></Button>
          <Button size="small" onClick={packageCustomize}><FontAwesomeIcon icon={faPen} /></Button>
        </div>
      </>
      const actionsLarge = <>
        <div className="actions">
          <Button kind="delete" onClick={packageRemove}>entfernen</Button>
          <Button onClick={packageCustomize}>anpassen</Button>
        </div>
      </>
      packageActions = <Media queries={{large: "(min-width: 640px)"}}>
        {matches => {
          if(matches.large) {
            return actionsLarge;
          }
          else {
            return actionsSmall;
          }
        }}
      </Media>
      
    }

    // participant count & price, discount, names of included activities
    const lis = [
      <li key="participants">{`${activityPackageInfo.priceConfiguration.participantCount} Teilnehmer:`} <Money amount={activityPackageInfo.price} showCurrency={false} /> (statt <Money amount={activityPackageInfo.activitiesTotal} showCurrency={false} />)</li>,
      // <li key="discount">Kombinationsrabatt: <Money amount={activityPackageInfo.discount} showCurrency={false} /></li>
    ];
    activityPackageInfo.activityNames.sort((a, b) => a.localeCompare(b)).forEach((name, index) => lis.push(<li key={index}>{name}</li>));
    
    // the row
    activityPackageRow = (
      <tr key="activityPackage">
        <td className="image">
          <Link to={`/packages/${activityPackageInfo.activityPackage.slug}`}>
            <img src={activityPackageInfo.activityPackage.image.url} alt={activityPackageInfo.activityPackage.title} />
          </Link>
        </td>
        <td>
          <div className="title">{activityPackageInfo.activityPackage.title}</div>
          <ul>
            {lis}
          </ul>
        </td>
        <td>
          <div><Money amount={activityPackageInfo.price} showCurrency={true} /></div>
          {packageActions}
        </td>
      </tr>
    );
    // the discount row 
    // activityPackageDiscountRow = (
    //   <tr key="activityPackageDiscount">
    //     <td>
    //     </td>
    //     <td>
    //       <div className="title">Kombinationationsrabatt:</div>
    //     </td>
    //     <td>
    //       <div>- <Money amount={activityPackageInfo.discount} showCurrency={true} /></div>
    //     </td>
    //   </tr>
    // )
  }

  // add more
  let addMore = null;
  if(showAddMore) {
    addMore = (
      <tr key="addMore" className="addMore">
        <td><div onClick={() => setRedirectTo("/")}>+</div></td>
        <td>Eine Appentura-Überraschung ist besonders spannend, wenn du mehrere Aktivitäten zu einem Erlebnis kombinierst.</td>
        <td></td>
      </tr>
    )
  }

  // total (as a table row)
  let total = null
  if(showTotal) {
    total = (
      <tr key="total" className="total">
        <td></td>
        <td className="title">TOTAL</td>
        <td>
          <Money amount={data.Cart.getTotalCost()} displayVatInfo={true} total />
        </td>
      </tr>
    )
  }

  // subtotal
  let subtotal = null
  if(showTotal && (showShipping || s.price > 0)) {
    let shippingPrice = (data.Cart.getShippingType() || {price:0}).price
    subtotal = (
      <tr key="subtotal" className="subtotal">
        <td></td>
        <td className="title">Bestellwert</td>
        <td>
          <Money amount={data.Cart.getTotalCost() - shippingPrice} />
        </td>
      </tr>
    )
  }

  // render
  return (
    <>
      <div id="s-checkoutitems">
        <table><tbody>
          {activities}
          {activityPackageRow}
          {activityPackageDiscountRow}
          {voucherRow}
          {subtotal}
          {shippingInfo}
          {addMore}
          {total}
        </tbody></table>
        {shippingEta}
      </div>
      
      <Overlay
        visible={activityToCustomize !== null}
        close={() => setActivityToCustomize(null)}
        position = "fill"
        closeOnClickOutside={false}
      >
        <Customize 
          activity={activityToCustomize} 
          cancel={() => setActivityToCustomize(null)} 
          close={() => setActivityToCustomize(null)} 
        />
      </Overlay>
    </>
  )
}

CheckoutItems.propTypes = {
  showActions: PropTypes.bool.isRequired, 
  showShipping: PropTypes.bool.isRequired,
  showTotal: PropTypes.bool.isRequired,
  showVoucher: PropTypes.bool.isRequired,
  showShippingEta: PropTypes.bool.isRequired
}

CheckoutItems.defaultProps = {
  showActions: true, 
  showShipping: true, 
  showTotal: true, 
  showVoucher: true,
  showShipppingEta: true
}

export default CheckoutItems